<template>
  <block
    component-name="block-carousel-products"
    ref="tpl_root"
    :px="false"
    body-class="flex w-full portrait:flex-col-reverse max-h-small-screen h-small-screen contain-strict bg-skin-base"
    body-skin="black"
  >
    <template v-slot:default="blockData">
      <!-- CAROUSEL MINIMAL -->
      <section
        class="relative top-0 left-0 grid w-1/2 max-h-small-screen h-small-screen portrait:h-[50vh] portrait:supports-[height:50svh]:h-[50svh] portrait:max-h-[50vh] portrait:supports-[max-height:50svh]:max-h-[50svh] grid-cols-1 portrait:w-full text-skin-base"
      >
        <swiper-container
          init="false"
          :id="containerLeftUniqId"
          class="w-full h-full max-h-full col-start-1 col-end-2 row-start-1 row-end-2"
          events-prefix="swiper-"
        >
          <swiper-slide
            v-for="(item, index) in $attrs.data.body.items"
            class="grid grid-cols-1 overflow-hidden max-h-small-screen h-small-screen portrait:h-[50vh] portrait:supports-[height:50svh]:h-[50svh] portrait:max-h-[50vh] portrait:supports-[max-height:50svh]:max-h-[50svh]"
          >
            <div
              class="relative top-0 left-0 w-full h-full col-start-1 col-end-2 row-start-1 row-end-2 pointer-events-none"
            >
              <div
                v-if="item.atelier || item.resource"
                class="absolute inset-0 w-full h-full p-edge z-2 grid grid-cols-1 grid-rows-1 border"
              >
                <div
                  class="col-start-1 col-end-2 row-start-1 row-end-2 flex flex-col laptop:flex-row justify-start items-start gap-sm pointer-events-auto w-full max-h-min"
                >
                  <chip-label
                    v-if="item.atelier"
                    variant="text"
                    target="atelier"
                  />

                  <chip-label
                    v-if="item.resource"
                    variant="text"
                    target="resource"
                  />
                </div>

                <!-- RESOURCE SVG -->
                <wrapper-reveal
                  v-if="item.resource"
                  class="col-start-1 col-end-2 row-start-1 row-end-2 w-full flex justify-end items-start"
                  reveal="fade-from-bottom"
                  :delay="200"
                  inert
                >
                  <svg-resource-badge />
                </wrapper-reveal>
                <!-- end RESOURCE SVG -->
              </div>

              <transition-fade
                v-if="item.indoor && item.outdoor"
                use-mode="in-out"
                duration="slow"
              >
                <media
                  key="indoor"
                  v-if="isIndoor"
                  v-bind="item.indoor.minimal.asset"
                  :sizes="sizes"
                  :loading="
                    index > 0 || blockData?.loading === 'lazy'
                      ? 'lazy'
                      : 'eager'
                  "
                  aspect-ratio="none"
                  :use-bg="false"
                  class="absolute inset-0 w-full h-full"
                />
                <media
                  v-else
                  key="outdoor"
                  v-bind="item.outdoor.minimal.asset"
                  :sizes="sizes"
                  :loading="
                    index > 0 || blockData?.loading === 'lazy'
                      ? 'lazy'
                      : 'eager'
                  "
                  aspect-ratio="none"
                  :use-bg="false"
                  class="absolute inset-0 w-full h-full"
                />
              </transition-fade>

              <media
                v-else-if="item.indoor"
                v-bind="item.indoor.minimal.asset"
                :sizes="sizes"
                :loading="
                  index > 0 || blockData?.loading === 'lazy' ? 'lazy' : 'eager'
                "
                aspect-ratio="none"
                :use-bg="false"
                class="w-full h-full max-w-full max-h-full overflow-hidden"
              />

              <media
                v-else-if="item.outdoor"
                v-bind="item.outdoor.minimal.asset"
                :sizes="sizes"
                :loading="
                  index > 0 || blockData?.loading === 'lazy' ? 'lazy' : 'eager'
                "
                aspect-ratio="none"
                :use-bg="false"
                class="w-full h-full max-w-full max-h-full overflow-hidden"
              />
            </div>
          </swiper-slide>
        </swiper-container>

        <footer
          v-for="(item, index) in $attrs.data.body.items"
          class="relative top-0 left-0 flex items-end justify-between flex-none w-full h-full max-w-full max-h-full col-start-1 col-end-2 row-start-1 row-end-2 pointer-events-none gap-lg z-2 p-edge overlay-gradient-from-bottom"
          :class="{
            'visible z-[2]': currentIndex === index,
            invisible: currentIndex !== index,
          }"
        >
          <!-- LEFT CONTENT -->
          <div class="max-w-[70%]">
            <wrapper-roomvo
              v-if="
                $attrs.config.useRoomvo &&
                item.button.action.roomvo?.type &&
                item.button.action.roomvo?.sku
              "
              :str="item.button.action.roomvo.type"
              :sku="item.button.action.roomvo.sku"
              class="pointer-events-auto"
            >
              <btn-pill
                data-skin="black"
                size="small"
                :filled="true"
                :title="$t.labels.try_it_in_your_environment"
                icon="configurator"
              />
            </wrapper-roomvo>

            <btn-pill
              v-else-if="item.button?.action?.to"
              data-skin="black"
              size="small"
              :filled="true"
              :title="item.button.title"
              :action="item.button.action"
              icon="arrow-long-right"
              class="pointer-events-auto"
            />
          </div>
          <!-- end LEFT CONTENT -->

          <!-- RIGHT CONTENT -->
          <div
            class="relative top-0 left-0 flex flex-col items-end justify-start z-2 gap-md min-w-min"
          >
            <div class="relative top-0 left-0 pointer-events-auto group/label">
              <div
                class="overflow-hidden absolute top-0 left-[100%] h-full -translate-x-full rounded-full pointer-events-none"
              >
                <div
                  class="transition-transform duration-300 translate-x-full group-hover/label:delay-150 group-hover/label:!translate-x-0 flex items-center h-full text-body/none bg-white/75 text-skin-inverse pl-lg pr-4xl whitespace-nowrap rounded-full select-none"
                  v-html="$t.labels.add_to_moodboard"
                ></div>
              </div>
              <btn-add-to-moodboard
                class="relative top-0 left-0"
                :slug="item.slug"
              />
            </div>

            <div class="relative top-0 left-0 pointer-events-none group/label">
              <div
                class="overflow-hidden absolute top-0 left-[100%] h-full -translate-x-full rounded-full pointer-events-none"
              >
                <div
                  class="transition-transform duration-300 translate-x-full group-hover/label:delay-150 group-hover/label:!translate-x-0 flex items-center h-full text-body/none bg-white/75 text-skin-inverse pl-lg pr-4xl whitespace-nowrap rounded-full select-none"
                  v-html="$t.labels.add_to_favourites"
                ></div>
              </div>

              <btn-handle-favourite
                class="relative top-0 left-0 pointer-events-auto"
                :slug="item.slug"
              />
            </div>

            <div class="relative top-0 left-0 pointer-events-auto group/label">
              <div
                class="overflow-hidden absolute top-0 left-[100%] h-full -translate-x-full rounded-full pointer-events-none"
              >
                <div
                  class="transition-transform duration-300 translate-x-full group-hover/label:delay-150 group-hover/label:!translate-x-0 flex items-center h-full text-body/none bg-white/75 text-skin-inverse pl-lg pr-4xl whitespace-nowrap rounded-full select-none"
                  v-html="$t.labels.download"
                ></div>
              </div>

              <btn-icon
                icon="download"
                :title="$t.labels.download"
                :action="downloadAction"
                variant="rounded-filled"
                class="relative top-0 left-0"
              />
            </div>
          </div>
          <!-- end RIGHT CONTENT -->
        </footer>
      </section>
      <!-- end CAROUSEL MINIMAL -->

      <!-- CAROUSEL AMBIENT -->
      <section
        class="relative top-0 left-0 grid grid-cols-1 w-1/2 max-h-small-screen h-small-screen portrait:h-[50vh] portrait:supports-[height:50svh]:h-[50svh] portrait:max-h-[50vh] portrait:supports-[max-height:50svh]:max-h-[50svh] portrait:w-full text-skin-base"
        :class="`landscape:[&_*::part(pagination)]:invisible 
          [&_*::part(pagination)]:right-edge 
          [&_*::part(pagination)]:bottom-edge 
          [&_*::part(pagination)]:left-auto 
          [&_*::part(pagination)]:!m-0 
          [&_*::part(pagination)]:!p-0 
          [&_*::part(pagination)]:h-[22px] 
          [&_*::part(pagination)]:max-w-[76px] 
          [&_*::part(bullet)]:bg-skin-inverse 
          [&_*::part(bullet)]:opacity-50 
          [&_*::part(bullet)]:scale-100
          [&_*::part(bullet-active)]:bg-skin-inverse
        `"
      >
        <swiper-container
          init="false"
          :id="containerRightUniqId"
          class="w-full h-full max-h-full col-start-1 col-end-2 row-start-1 row-end-2"
          events-prefix="swiper-"
        >
          <span
            slot="container-end"
            inert
            class="absolute inset-0 hidden w-full h-full z-2 overlay-gradient-from-bottom"
          >
          </span>

          <swiper-slide
            v-for="(item, index) in $attrs.data.body.items"
            class="overflow-hidden pointer-events-none max-h-small-screen h-small-screen portrait:h-[50vh] portrait:supports-[height:50svh]:h-[50svh] portrait:max-h-[50vh] portrait:supports-[max-height:50svh]:max-h-[50svh]"
          >
            <div
              class="relative top-0 left-0 w-full h-full pointer-events-none z-1"
              data-swiper-parallax-x="50%"
            >
              <transition-fade
                v-if="item.indoor && item.outdoor"
                use-mode="in-out"
                duration="slow"
              >
                <media
                  key="indoor"
                  v-if="isIndoor"
                  :use-bg="false"
                  v-bind="item.indoor.ambient.asset"
                  :sizes="sizes"
                  :loading="
                    index > 0 || blockData?.loading === 'lazy'
                      ? 'lazy'
                      : 'eager'
                  "
                  aspect-ratio="none"
                  class="absolute inset-0 w-full h-full"
                />
                <media
                  v-else
                  key="outdoor"
                  :use-bg="false"
                  v-bind="item.outdoor.ambient.asset"
                  :sizes="sizes"
                  :loading="
                    index > 0 || blockData?.loading === 'lazy'
                      ? 'lazy'
                      : 'eager'
                  "
                  aspect-ratio="none"
                  class="absolute inset-0 w-full h-full"
                />
              </transition-fade>

              <media
                v-else-if="item.indoor"
                v-bind="item.indoor.ambient.asset"
                :sizes="sizes"
                :loading="
                  index > 0 || blockData?.loading === 'lazy' ? 'lazy' : 'eager'
                "
                aspect-ratio="none"
                class="w-full h-full max-w-full max-h-full overflow-hidden"
              />

              <media
                v-else-if="item.outdoor"
                v-bind="item.outdoor.ambient.asset"
                :sizes="sizes"
                :loading="
                  index > 0 || blockData?.loading === 'lazy' ? 'lazy' : 'eager'
                "
                aspect-ratio="none"
                class="w-full h-full max-w-full max-h-full overflow-hidden"
              />
            </div>

            <span
              inert
              class="absolute inset-0 w-full h-full z-2 overlay-gradient-from-bottom"
            ></span>

            <!-- CONTROLS -->
            <footer
              class="absolute inset-0 z-10 flex items-end w-full h-full pointer-events-none p-edge"
              :class="{
                'justify-center': $attrs.data.body.items.length === 1,
                'justify-between': $attrs.data.body.items.length > 1,
              }"
            >
              <div
                class="w-fit h-fit transition-opacity duration-500 ease-in-out opacity-0 [.swiper-slide-active_&]:pointer-events-auto [.swiper-slide-active_&]:opacity-100"
              >
                <client-only>
                  <btn-indoor-outdoor
                    :indoor="
                      item.indoor && item.outdoor
                        ? isIndoor
                        : item.indoor
                        ? true
                        : false
                    "
                    :disable-unactive="!(item.indoor && item.outdoor)"
                    :disabled="!(item.indoor && item.outdoor)"
                    @update="handleIsIndoor"
                  />
                </client-only>
              </div>
              <span inert v-if="attrs.data.body.items.length > 1"></span>
            </footer>
            <!-- end CONTROLS -->
          </swiper-slide>
        </swiper-container>

        <!-- CONTROLS -->
        <footer
          v-if="attrs.data.body.items.length > 1"
          class="relative top-0 left-0 flex items-end justify-end w-full h-full col-start-1 col-end-2 row-start-1 row-end-2 overflow-hidden pointer-events-none z-2 p-edge"
        >
          <div class="flex pointer-events-auto portrait:hidden gap-lg">
            <btn-arrow
              :disabled="currentIndex === 0"
              direction="prev"
              @click="goPrev"
            />

            <btn-arrow
              :disabled="currentIndex === attrs.data.body.items.length - 1"
              direction="next"
              @click="goNext"
            />
          </div>
        </footer>
        <!-- end CONTROLS -->
      </section>
      <!-- end CAROUSEL AMBIENT -->
    </template>
  </block>
</template>

<script setup>
import { useUserStore } from "@/stores/user";

const userStore = useUserStore();
const attrs = useAttrs();
const tpl_root = templateRef("tpl_root");
const isIndoor = ref(true);
const currentIndex = ref(0);
const disableSwitch = ref(false);

const containerRightUniqId = ref(
  `block-carousel-products_swiper-container-ambient_${useId().replace(
    /[^a-zA-Z0-9]/g,
    "_"
  )}`
);
const containerLeftUniqId = ref(
  `block-carousel-products_swiper-container-minimal_${useId().replace(
    /[^a-zA-Z0-9]/g,
    "_"
  )}`
);

function getAction(item) {
  if (item.indoor && item.outdoor) {
    if (isIndoor.value) {
      return item.indoor.minimal.asset;
    } else {
      return item.outdoor.minimal.asset;
    }
  } else if (item.indoor) {
    return item.indoor.minimal.asset;
  } else if (item.outdoor) {
    return item.outdoor.minimal.asset;
  }
}

const downloadAction = computed(() => {
  const {
    alt: filename,
    cdn,
    src: href,
  } = getAction(attrs.data.body.items[currentIndex.value]);
  return { filename, cdn, href, type: "image" };
});

const isPortrait = useMediaQuery("(orientation: portrait)");

const sizes = computed(() => {
  let obj = {};

  if (isPortrait.value) {
    obj = {
      base: 600,
      phablet: 1020,
      tablet: 1272,
      laptop: 1512,
      desktop: 1920,
      extdesktop: 2560,
    };
  } else {
    obj = {
      base: 300,
      phablet: 510,
      tablet: 636,
      laptop: 756,
      desktop: 960,
      extdesktop: 1280,
    };
  }

  return obj;
});

let swiperMinimalEl, swiperAmbientEl;

function handleIsIndoor(val) {
  isIndoor.value = val;
}

function goPrev() {
  if (swiperAmbientEl) swiperAmbientEl.swiper.slidePrev();
}

function goNext() {
  if (swiperAmbientEl) swiperAmbientEl.swiper.slideNext();
}

useSafeMountedEl(tpl_root, () => {
  swiperMinimalEl = tpl_root.value.$el.querySelectorAll("swiper-container")[0];
  swiperAmbientEl = tpl_root.value.$el.querySelectorAll("swiper-container")[1];
  Object.assign(swiperMinimalEl, {
    controller: {
      control: `#${containerRightUniqId.value}`,
    },
    slidesPerView: 1,
    preventInteractionOnTransition: true,
    loop: false,
    parallax: attrs.data.body.items.length > 1,
    allowTouchMove: false,
    followFinger: false,
    simulateTouch: false,
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    speed: 650,
    on: {
      init() {
        ScrollTrigger.refresh(true);
      },
    },
  });

  Object.assign(swiperAmbientEl, {
    controller: {
      control: `#${containerLeftUniqId.value}`,
    },
    slidesPerView: 1,
    preventInteractionOnTransition: true,
    grabCursor: attrs.data.body.items.length > 1,
    loop: false,
    parallax: attrs.data.body.items.length > 1,
    allowTouchMove: attrs.data.body.items.length > 1,
    pagination:
      attrs.data.body.items.length > 1 ? { dynamicBullets: true } : false,
    speed: 650,
    on: {
      init() {
        ScrollTrigger.refresh(true);
      },
      slideChange(swiper) {
        currentIndex.value = swiper.realIndex;
      },
    },
  });

  swiperMinimalEl.initialize();
  swiperAmbientEl.initialize();
});

onBeforeUnmount(() => {
  swiperMinimalEl?.swiper?.destroy();
  swiperMinimalEl = null;

  swiperAmbientEl?.swiper?.destroy();
  swiperAmbientEl = null;
});
</script>
